import localize from '../../../../common/localize';
import {
  GetDateInMMDDYYYYFormat,
  getExpectedSolutionDate,
  getOperationalStatusNameFromId,
  getValueInCurrency,
} from '../../../../common/utils';
import ReportSectionTitle from '../../../../components/common/ReportSectionTitle';
import {
  getAdditionalCostsTotalValue,
  getCurrencyFromSetup,
  getEventTotalValue,
} from '../../../../common/EntitiesManagement';
import InfoRowData from './InfoRowData';

export default function ({ event, statistics, additionalCosts }) {
  const getDateValue = (fieldValue) =>
    new Date(fieldValue).toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const valueOrNoData = (value) => {
    if (value && value.length > 0) {
      return value;
    }

    return localize.no_data;
  };

  const getPersonalFullName = (personal) => {
    if (personal) {
      return `${personal?.SurName} ${personal?.LastName}`;
    }
    return localize.no_data;
  };

  const getVehicleData = (vehicle) => {
    if (vehicle) {
      return `(${vehicle.VehicleType?.Name}) ${vehicle.Identification}`;
    }

    return localize.no_data;
  };

  const generateTableData = () => {
    const eventData = [];
    eventData.push({
      Id: 1,
      firstFieldName: `${localize.type}:`,
      firstFieldValue: event?.EventType?.Name ?? localize.no_data,
      lastFieldName: `${localize.status}:`,
      lastFieldValue: event?.CurrentStatus?.Name
        ? `${event?.CurrentStatus?.Name} (${getOperationalStatusNameFromId(
            event?.OperationalStatus
          )})`
        : localize.no_data,
    });

    eventData.push({
      Id: 3,
      firstFieldName: `${localize.creation_date}:`,
      firstFieldValue: event?.CreationDate
        ? GetDateInMMDDYYYYFormat(new Date(event?.CreationDate))
        : localize.no_data,
      lastFieldName: `${localize.expected_attention_date}:`,
      lastFieldValue: getExpectedSolutionDate(event),
    });

    eventData.push({
      Id: 6,
      firstFieldName: `${localize.reported_by}:`,
      firstFieldValue: valueOrNoData(event?.ReporterName),
      lastFieldName: `${localize.reporter_email}:`,
      lastFieldValue: valueOrNoData(event?.ReporterEmail),
    });

    eventData.push({
      Id: 5,
      firstFieldName: `${localize.address}:`,
      firstFieldValue: valueOrNoData(event?.Address ? 
                      `${event?.Address} ${event?.AddressComplement ?? ''}` 
                      : event?.AddressComplement ?? ''),
      lastFieldName: `${localize.reporter_phone_number}:`,
      lastFieldValue: valueOrNoData(event?.ReporterPhoneNumber),
    });

    eventData.push({
      Id: 4,
      firstFieldName: `${localize.city}:`,
      firstFieldValue: event?.City?.Name ?? localize.no_data,
      lastFieldName: `${localize.department}:`,
      lastFieldValue: event?.State?.Name ?? localize.no_data,
    });

    eventData.push({
      Id: 8,
      firstFieldName: `${localize.assigned_by}:`,
      firstFieldValue: event?.AssignedBy ?? localize.no_data,
      lastFieldName: `${localize.assigned_to}:`,
      lastFieldValue: getPersonalFullName(event?.PersonalAssigned),
    });

    if (event?.VehicleAssigned) {
      eventData.push({
        Id: 7,
        firstFieldName: `${localize.vehicle}:`,
        firstFieldValue: getVehicleData(event?.VehicleAssigned),
        lastFieldName: `${localize.driver}:`,
        lastFieldValue: event?.Driver ?? localize.no_data,
      });
    }

    eventData.push({
      Id: 9,
      firstFieldName: `${localize.modified_by}:`,
      firstFieldValue: event?.CreatedBy ?? localize.no_data,
      lastFieldName: `${localize.modification_date}:`,
      lastFieldValue: getDateValue(event?.ModificationDate),
    });

    return eventData;
  };

  const isBillableService = () =>
    event?.executionFinalValue > 0 ||
    event?.sparePartsFinalValue > 0 ||
    getAdditionalCostsTotalValue(additionalCosts) > 0;

  const generateBillData = () => {
    const billData = [];

    billData.push({
      Id: 2,
      firstFieldName: `${localize.service_value}:`,
      firstFieldValue: getValueInCurrency(
        getCurrencyFromSetup(),
        event?.executionFinalValue ?? 0
      ),
      lastFieldName: `${localize.spare_parts_value}:`,
      lastFieldValue: getValueInCurrency(
        getCurrencyFromSetup(),
        event?.sparePartsFinalValue ?? 0
      ),
    });

    billData.push({
      Id: 11,
      firstFieldName: `${localize.additional_costs}:`,
      firstFieldValue: getValueInCurrency(
        getCurrencyFromSetup(),
        getAdditionalCostsTotalValue(additionalCosts) ?? 0,
        true
      ),
      lastFieldName: `${localize.total_value}:`,
      lastFieldValue:
        getValueInCurrency(
          getCurrencyFromSetup(),
          getEventTotalValue(event, true),
          true
        ) ?? localize.no_data,
    });

    return billData;
  };

  const generateRowData = (
    registerId,
    leftField,
    rightField,
    maxHeightValue
  ) => (
    <InfoRowData
      key={`${registerId}-0`}
      registerId={registerId}
      leftField={leftField}
      rightField={rightField}
      maxHeightValue={maxHeightValue}
    />
  );

  return (
    <div>
      <ReportSectionTitle title={localize.general_information} />
      <div className="report_section_without_top_border">
        {generateTableData()?.map((row) =>
          generateRowData(
            row.Id,
            row.firstFieldName
              ? { Name: row.firstFieldName, Value: row.firstFieldValue }
              : null,
            row.lastFieldName && {
              Name: row.lastFieldName,
              Value: row.lastFieldValue,
            },
            '30px'
          )
        )}
      </div>

      {event?.Observation?.length > 0 && (
        <div>
          <ReportSectionTitle title={localize.observations} />
          <div className="report_section_without_top_border">
            {generateRowData(
              10,
              {
                Name: '',
                Value: valueOrNoData(event?.Observation),
              },
              null,
              '100px'
            )}
          </div>
        </div>
      )}

      {isBillableService() && (
        <div>
          <ReportSectionTitle title={localize.settlement} />
          <div className="report_section_without_top_border">
            {generateBillData()?.map((row) =>
              generateRowData(
                row.Id,
                row.firstFieldName
                  ? { Name: row.firstFieldName, Value: row.firstFieldValue }
                  : null,
                row.lastFieldName && {
                  Name: row.lastFieldName,
                  Value: row.lastFieldValue,
                },
                '30px'
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
